import { RuntimeHost } from '../plc/runtime/RuntimeHost';
import { compileLadderProgram } from '../ladder/compiler/compiler';
import { compileNetworkLogic } from '../ladder/compiler/graph';
import { evaluateExpression } from '../ladder/compiler/expression';
import type { LadderProgram } from '../ladder/model/LadderProgram';
import type { BooleanExpression } from '../ladder/compiler/expression';
import { parseAddress } from '../plc/address/parser';
import { ProcessSimulationEngine } from './process/ProcessSimulationEngine';
import { AlarmManager } from './alarms/AlarmManager';

export type SimulationState = 'STOPPED' | 'RUNNING' | 'PAUSED' | 'ERROR';

export type PowerFlowState = 'UNKNOWN' | 'INACTIVE' | 'ACTIVE' | 'FAULT';

export interface ElementRuntimeState {
  powerFlow: PowerFlowState;
  value?: boolean; // For coils/contacts real evaluated value
}

export interface SimulationSnapshot {
  state: SimulationState;
  cycle: number;
  scanTimeMs: number;
  simulationTimeMs: number;
  elementStates: Record<string, ElementRuntimeState>;
  timers?: Record<string, { elapsedMs: number; presetMs: number; q: boolean; running: boolean; }>;
  counters?: Record<string, { type: string; cv: number; pv: number; q: boolean }>;
  devices?: Record<string, Record<string, unknown>>;
  products?: { id: string; x: number; y: number; width: number; height: number; active: boolean; blocked?: boolean; blockedTimeMs?: number; conveyorId?: string }[];
  error?: string;
}

export class SimulationEngine {
  public host: RuntimeHost;
  public process: ProcessSimulationEngine;
  public alarms: AlarmManager;
  private timerId: number | null = null;
  
  public state: SimulationState = 'STOPPED';
  public cycle = 0;
  public scanTimeMs = 0;
  public simulationTimeMs = 0;
  public simulationSpeed = 1.0;
  public error?: string;

  private activeProgram: LadderProgram | null = null;
  private elementConditions = new Map<string, BooleanExpression>();

  constructor() {
    this.host = new RuntimeHost();
    this.process = new ProcessSimulationEngine();
    this.alarms = new AlarmManager();
  }

  public loadProgram(program: LadderProgram) {
    this.activeProgram = program;
    const { success, ir, diagnostics } = compileLadderProgram(program);
    if (!success) {
      this.state = 'ERROR';
      this.error = diagnostics.map(d => d.message).join('\n');
      return false;
    }

    this.host.loadProgram(ir);

    // Pre-calculate conditions for all elements to evaluate power flow
    this.elementConditions.clear();
    for (const net of program.networks) {
      const logic = compileNetworkLogic(net);
      for (const [elId, cond] of logic.elementConditions.entries()) {
        this.elementConditions.set(elId, cond);
      }
    }
    return true;
  }

  public start() {
    if (this.state === 'RUNNING') return;
    if (this.state === 'STOPPED') {
      this.host.start();
      this.cycle = 0;
      this.simulationTimeMs = 0;
    }
    this.state = 'RUNNING';
    delete this.error;
    this.alarms.logEvent(this.simulationTimeMs, 'SYSTEM', 'SIM_ENGINE', 'Simulation started');
    this.scheduleNextTick();
  }

  public pause() {
    this.state = 'PAUSED';
    if (this.timerId !== null) {
      window.clearTimeout(this.timerId);
      this.timerId = null;
    }
    this.alarms.logEvent(this.simulationTimeMs, 'SYSTEM', 'SIM_ENGINE', 'Simulation paused');
  }

  public stop() {
    this.state = 'STOPPED';
    if (this.timerId !== null) {
      window.clearTimeout(this.timerId);
      this.timerId = null;
    }
    this.host.stop();
    this.host.physicalInputs.fill(0);
    this.host.physicalOutputs.fill(0);
    this.process.reset();
    this.cycle = 0;
    this.simulationTimeMs = 0;
    delete this.error;
    this.alarms = new AlarmManager();
    this.alarms.logEvent(this.simulationTimeMs, 'SYSTEM', 'SIM_ENGINE', 'Simulation stopped');
  }

  public reset() {
    this.stop();
    this.alarms.logEvent(this.simulationTimeMs, 'SYSTEM', 'SIM_ENGINE', 'Simulation reset');
  }

  public step(deltaMs: number = 10) {
    if (this.state !== 'PAUSED') return;
    this.executeSingleCycle(deltaMs);
  }

  private scheduleNextTick() {
    if (this.state !== 'RUNNING') return;
    
    // Default base tick is 10ms
    const baseTickMs = 10;
    const realTickMs = baseTickMs / this.simulationSpeed;
    
    this.timerId = window.setTimeout(() => {
      this.executeSingleCycle(baseTickMs);
      this.scheduleNextTick();
    }, realTickMs);
  }

  private executeSingleCycle(deltaMs: number = 10) {
    const t0 = performance.now();
    try {
      this.host.execute(this.simulationTimeMs, deltaMs);
      this.process.tick(this.simulationTimeMs, deltaMs, this.host, this.alarms);
      this.cycle++;
      this.simulationTimeMs += deltaMs;
    } catch (e) {
      this.state = 'ERROR';
      this.error = e instanceof Error ? e.message : String(e);
      if (this.timerId !== null) {
        window.clearTimeout(this.timerId);
        this.timerId = null;
      }
    }
    const t1 = performance.now();
    this.scanTimeMs = t1 - t0;
  }

  public getSnapshot(): SimulationSnapshot {
    const elementStates: Record<string, ElementRuntimeState> = {};
    
    if (this.activeProgram) {
      for (const net of this.activeProgram.networks) {
        for (const el of Object.values(net.elements)) {
          const cond = this.elementConditions.get(el.id);
          let powerFlow: PowerFlowState = 'UNKNOWN';
          let value: boolean | undefined = undefined;

          if (cond) {
            const isFlowing = this.state !== 'STOPPED' && evaluateExpression(cond, this.host);
            powerFlow = isFlowing ? 'ACTIVE' : 'INACTIVE';
          }

          if (el.type === 'NO_CONTACT' || el.type === 'NC_CONTACT' || el.type === 'COIL' || el.type === 'SET_COIL' || el.type === 'RESET_COIL' || el.type === 'TON' || el.type === 'TOF' || el.type === 'TP' || el.type === 'CTU' || el.type === 'CTD' || el.type === 'CTUD') {
            const addrStr = (el as { address?: string }).address;
            if (addrStr) {
              const addr = parseAddress(addrStr);
              if (addr && addr.size === 'X') {
                let val = false;
                switch (addr.area) {
                  case 'I': val = this.host.pi.inputs.getBit(addr.byteOffset, addr.bitOffset); break;
                  case 'Q': val = this.host.pi.outputs.getBit(addr.byteOffset, addr.bitOffset); break;
                  case 'M': val = this.host.pi.memory.getBit(addr.byteOffset, addr.bitOffset); break;
                  case 'T': {
                    const id = `T${addr.byteOffset}`;
                    const t = this.host.timers.getTimer(id);
                    val = t ? t.output : false;
                    break;
                  }
                  case 'C': {
                    const id = `C${addr.byteOffset}`;
                    const c = this.host.counters.getCounter(id);
                    val = c ? c.q : false;
                    break;
                  }
                }
                value = val;
              } else if (addr && addr.area === 'T') {
                const id = `T${addr.byteOffset}`;
                const t = this.host.timers.getTimer(id);
                value = t ? t.output : false;
              } else if (addr && addr.area === 'C') {
                const id = `C${addr.byteOffset}`;
                const c = this.host.counters.getCounter(id);
                value = c ? c.q : false;
              }
            }
          }
          
          elementStates[el.id] = value !== undefined ? { powerFlow, value } : { powerFlow };
        }
      }
    }

    const deviceStates: Record<string, Record<string, unknown>> = {};
    for (const [id, device] of this.process.devices.entries()) {
      deviceStates[id] = { ...device.getState() };
    }

    return {
      state: this.state,
      cycle: this.cycle,
      scanTimeMs: this.scanTimeMs,
      simulationTimeMs: this.simulationTimeMs,
      elementStates,
      timers: this.host.timers.getSnapshot(),
      counters: this.host.counters.getSnapshot(),
      devices: deviceStates,
      products: this.process.products.map(p => ({ ...p })),
      ...(this.error !== undefined && { error: this.error })
    };
  }
}
